import { CurrencyPipe } from "@angular/common";
import {
	Directive,
	ElementRef,
	HostListener,
	Input,
	OnDestroy,
	OnInit
} from "@angular/core";
import { NgControl } from "@angular/forms";
import { Observable, Subject, filter, takeUntil } from "rxjs";

@Directive({
	selector: "[appCurrency]",
	providers: [CurrencyPipe],
	standalone: false
})
export class CurrencyDirective implements OnInit, OnDestroy {
	@Input()
	updateCurrencyProgrammatically = false;

	@Input()
	decimalPlaces = 2;

	private controlChanges$: Observable<number> | null = null;

	private _onDestroy = new Subject<boolean>();

	constructor(
		public el: ElementRef<HTMLInputElement>,
		private currencyPipe: CurrencyPipe,
		private ngControl: NgControl
	) {}

	ngOnInit(): void {
		if (this.updateCurrencyProgrammatically) {
			const brlCurrency = this.convertToBrl(this.el.nativeElement.value);

			if (brlCurrency) {
				this.format(brlCurrency);
			}

			this.controlChanges$ = this.ngControl.valueChanges;
			if (this.controlChanges$) {
				this.controlChanges$
					.pipe(
						filter((value) => typeof value === "number"),
						takeUntil(this._onDestroy)
					)
					.subscribe((value: number) => {
						this.format(value.toFixed(this.decimalPlaces));
					});
			}
		}
	}

	ngOnDestroy(): void {
		this._onDestroy.next(true);
	}

	@HostListener("input", ["$event.target.value"])
	onInput(eventValue: string): void {
		this.format(eventValue);
	}

	@HostListener("paste", ["$event"])
	onPaste(event: ClipboardEvent): void {
		event.preventDefault();
		if (event.clipboardData) {
			this.format(event.clipboardData.getData("text/plain"));
		}
	}

	pickWriteValueOptions(): {
		emitModelToViewChange: boolean;
		emitEvent?: boolean;
	} {
		if (this.updateCurrencyProgrammatically) {
			return {
				emitModelToViewChange: false,
				emitEvent: false
			};
		}
		return { emitModelToViewChange: false };
	}

	parse(val: string): number {
		return parseInt(String(val).replace(new RegExp("[.,]", "g"), ""));
	}

	convertToBrl(val: string | number): string | undefined {
		const decimalFormat = `1.${this.decimalPlaces}-${this.decimalPlaces}`;
		return this.currencyPipe
			.transform(val, "BRL", "symbol", decimalFormat)
			?.replace("R$", "")
			.trim();
	}

	format(val: string): void {
		if (val) {
			const numberFormat =
				this.parse(val) / Math.pow(10, this.decimalPlaces);
			const brlCurrency = this.convertToBrl(numberFormat);

			this.ngControl.valueAccessor?.writeValue(brlCurrency);
			this.ngControl.control?.setValue(
				numberFormat,
				this.pickWriteValueOptions()
			);
		}
	}
}
